import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/SimpleToast";
import { mailingApi } from "@/lib/api";
import { BarChart3, ArrowLeft, Loader2, MailOpen, MousePointerClick, Users, Star, CheckCircle2, Clock } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-lg border p-3">
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className={`w-3.5 h-3.5 ${accent || ""}`} /> {label}
    </div>
    <div className="text-2xl font-bold mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const RateBar = ({ label, rate, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs"><span>{label}</span><span className="font-medium">{rate}%</span></div>
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, rate)}%`, background: color }} />
    </div>
  </div>
);

export const CampaignDashboard = ({ open, onOpenChange }) => {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mailingApi.getCampaigns();
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Kampagnen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (open) { setDetail(null); load(); } }, [open, load]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const res = await mailingApi.getCampaign(id);
      setDetail(res.data);
    } catch (e) {
      toast.error("Detail konnte nicht geladen werden");
    } finally {
      setLoadingDetail(false);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "—");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="campaign-dashboard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detail && (
              <button type="button" onClick={() => setDetail(null)} className="text-muted-foreground hover:text-foreground" data-testid="campaign-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <BarChart3 className="w-5 h-5" /> {detail ? "Kampagnen-Detail" : "Kampagnen"}
          </DialogTitle>
          <DialogDescription>
            {detail ? detail.campaign?.subject : "Öffnungs- und Klickraten deiner versendeten Rundmails."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : detail ? (
          <ScrollArea className="flex-1 min-h-0 pr-3">
            {loadingDetail ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat icon={Users} label="Empfänger" value={detail.campaign.recipients_count} />
                  <Stat icon={CheckCircle2} label="Zugestellt" value={detail.campaign.delivered_count} accent="text-emerald-600" />
                  <Stat icon={MailOpen} label="Öffnungen" value={detail.campaign.open_count} sub={`${detail.campaign.open_rate}%`} accent="text-primary" />
                  <Stat icon={MousePointerClick} label="Klicks" value={detail.campaign.click_count} sub={`${detail.campaign.click_rate}%`} accent="text-brand-orange" />
                </div>
                <div className="rounded-lg border">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                    <span>Empfänger</span><span>Geöffnet</span><span>Geklickt</span>
                  </div>
                  {(detail.recipients || []).map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-sm border-b last:border-0 items-center" data-testid={`campaign-recipient-${i}`}>
                      <span className="truncate flex items-center gap-1.5">
                        {r.is_important && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500 shrink-0" />}
                        <span className="truncate">{r.name || r.email}</span>
                      </span>
                      <span className="w-16 text-center">{r.opened ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : "—"}</span>
                      <span className="w-14 text-center">{r.clicked ? `${r.click_count}×` : "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ScrollArea>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">Noch keine Kampagnen versendet.</div>
        ) : (
          <ScrollArea className="flex-1 min-h-0 pr-3">
            <div className="space-y-2">
              {campaigns.map((c) => (
                <button key={c.id} type="button" onClick={() => openDetail(c.id)}
                  className="w-full text-left rounded-lg border p-3 hover:border-primary/40 hover:bg-muted/30 transition-colors" data-testid={`campaign-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{c.group_name} · {fmt(c.sent_at)}</div>
                    </div>
                    {c.status === "sending" ? (
                      <Badge variant="outline" className="shrink-0 text-amber-600 border-amber-300"><Clock className="w-3 h-3 mr-1" /> sendet…</Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0">{c.recipients_count} Empf.</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <RateBar label="Öffnungsrate" rate={c.open_rate} color="hsl(var(--primary))" />
                    <RateBar label="Klickrate" rate={c.click_rate} color="#F39200" />
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        )}

        {!detail && (
          <div className="pt-2 border-t flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={load} data-testid="campaign-refresh-btn">Aktualisieren</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
